<template>
  <div class="container-fluid p-3 p-md-4">
    <DrawdownGuardBanner :guard="ddGuard" />

    <div class="d-flex justify-content-between align-items-start gap-2 mb-4 flex-wrap">
      <div>
        <h2 class="mb-0"><i :class="[NAV_ITEMS.Watchlist.icon, NAV_ITEMS.Watchlist.colorClass, 'me-2']"></i>Watchlist</h2>
        <small class="text-muted">
          Qualified tickers built by mercury-ai-researcher for today's session ·
          <router-link to="/watchlist/history">past sessions</router-link>
        </small>
      </div>
      <div class="d-flex align-items-center gap-2 gap-md-3 flex-wrap justify-content-end">
        <DrawdownGuardBadge :guard="ddGuard" />
        <div class="input-group input-group-sm watchlist-search">
          <span class="input-group-text bg-white">
            <i class="fas fa-search text-muted"></i>
          </span>
          <input
            v-model="search"
            type="search"
            class="form-control"
            placeholder="Search ticker"
            aria-label="Search ticker"
          />
        </div>
        <small class="text-muted">
          <i class="fas fa-clock me-1"></i>
          Last built:
          <span class="fw-semibold">{{ formattedLastBuiltAt }}</span>
        </small>
        <div class="input-group input-group-sm csv-direction" title="Direction applied to CSV rows that don't specify one">
          <span class="input-group-text bg-white"><i class="fas fa-compass text-muted"></i></span>
          <select v-model="defaultDirection" class="form-select" aria-label="Default direction for CSV upload">
            <option value="up">Bullish</option>
            <option value="down">Bearish</option>
          </select>
        </div>
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="triggerUpload"
          :disabled="uploading || entryHalted"
          :title="entryHalted ? entryHaltTooltip : ''"
        >
          <i class="fas me-1" :class="uploading ? 'fa-spinner fa-spin' : 'fa-file-csv'"></i>
          {{ uploading ? 'Uploading…' : 'Suggest via CSV' }}
        </button>
        <input
          ref="csvInput"
          type="file"
          accept=".csv,text/csv"
          class="d-none"
          @change="onFileSelected"
        />
        <button class="btn btn-outline-primary btn-sm" @click="refresh" :disabled="loading">
          <i class="fas fa-sync-alt me-1" :class="{ 'fa-spin': loading }"></i> Refresh
        </button>
      </div>
    </div>

    <!-- One-off single-ticker suggestion -->
    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <div v-if="manualError" class="alert alert-danger alert-dismissible py-2">
          <i class="fas fa-exclamation-triangle me-2"></i>{{ manualError }}
          <button type="button" class="btn-close" @click="manualError = null"></button>
        </div>
        <div v-if="manualResult" class="alert py-2" :class="manualResult.ok ? 'alert-success' : 'alert-warning'">
          <i class="fas me-2" :class="manualResult.ok ? 'fa-check-circle' : 'fa-info-circle'"></i>
          {{ manualResult.message }}
        </div>
        <form class="row g-2 align-items-end" @submit.prevent="submitManual">
          <div class="col-12 col-md-5">
            <label for="manualTicker" class="form-label mb-1 small fw-semibold">Suggest a ticker</label>
            <div class="position-relative">
              <input
                id="manualTicker"
                type="text"
                class="form-control"
                v-model="tickerQuery"
                @input="onTickerInput"
                @keydown.escape="showDropdown = false"
                @blur="onTickerBlur"
                autocomplete="off"
                placeholder="e.g. AAPL"
              />
              <div v-if="searchLoading" class="position-absolute top-50 end-0 translate-middle-y pe-2">
                <div class="spinner-border spinner-border-sm text-secondary" role="status">
                  <span class="visually-hidden">Searching...</span>
                </div>
              </div>
              <ul
                v-if="showDropdown && suggestions.length > 0"
                class="list-group shadow-sm"
                style="position: absolute; top: 100%; left: 0; right: 0; z-index: 1000;"
              >
                <li
                  v-for="item in suggestions.slice(0, 10)"
                  :key="item.ticker"
                  class="list-group-item list-group-item-action"
                  style="cursor: pointer;"
                  @mousedown.prevent="selectSuggestion(item)"
                >
                  <span class="fw-semibold">{{ item.ticker }}</span> &mdash; {{ item.name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <label for="manualDirection" class="form-label mb-1 small fw-semibold">Direction</label>
            <select id="manualDirection" v-model="manualDirection" class="form-select">
              <option value="up">Bullish</option>
              <option value="down">Bearish</option>
            </select>
          </div>
          <div class="col-6 col-md-2">
            <button
              type="submit"
              class="btn btn-primary w-100"
              :disabled="!canSubmitManual || entryHalted"
              :title="entryHalted ? entryHaltTooltip : ''"
            >
              <span v-if="manualSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Suggest
            </button>
          </div>
        </form>
        <div v-if="entryHalted" class="form-text text-danger mt-2">
          <i class="fas fa-ban me-1"></i>{{ entryHaltTooltip }}
        </div>
        <div class="form-text mt-2">
          One-off suggestions run through the same news qualification as CSV uploads. If news is found for
          the ticker the news direction overrides your choice; otherwise your direction is used. Only
          SPY/QQQ/DIA/IWM constituents are accepted.
        </div>
      </div>
    </div>

    <div v-if="uploadError" class="alert alert-danger alert-dismissible">
      <i class="fas fa-exclamation-triangle me-2"></i>{{ uploadError }}
      <button type="button" class="btn-close" @click="uploadError = null"></button>
    </div>

    <div v-if="uploadResult" class="alert alert-info alert-dismissible">
      <button type="button" class="btn-close" @click="uploadResult = null"></button>
      <div class="fw-semibold mb-1">
        <i class="fas fa-file-csv me-2"></i>
        Queued {{ uploadResult.enqueued }} ticker(s) for the researcher to qualify.
      </div>
      <div v-if="uploadResult.accepted.length" class="small mb-1">
        Accepted: <span class="fw-semibold">{{ uploadResult.accepted.join(', ') }}</span>
      </div>
      <div v-if="uploadResult.rejected.length" class="small">
        <span class="text-danger fw-semibold">Rejected:</span>
        <ul class="mb-0">
          <li v-for="r in uploadResult.rejected" :key="r.ticker">
            <span class="fw-semibold">{{ r.ticker }}</span> — {{ r.reason }}
          </li>
        </ul>
      </div>
      <div class="small text-muted mt-1">
        CSV format: one ticker per row, or a <code>ticker</code> column with an optional
        <code>direction</code> column (<code>bullish</code>/<code>bearish</code>). Rows without a direction
        use the selected default. Only SPY/QQQ/DIA/IWM constituents are accepted; queued tickers appear here
        once they pass news qualification. When news is found its direction overrides the suggested one.
      </div>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else-if="entries.length === 0" class="alert alert-info">
      <i class="fas fa-info-circle me-2"></i>
      The watchlist is currently empty. Nothing has been qualified for review yet.
    </div>

    <div v-else-if="filteredEntries.length === 0" class="alert alert-warning">
      <i class="fas fa-search me-2"></i>
      No tickers match "{{ search }}".
    </div>

    <div v-else class="card shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="table-light">
              <tr>
                <th>Ticker</th>
                <th>Sentiment</th>
                <th>Direction</th>
                <th>Reason</th>
                <th>Index</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in filteredEntries" :key="`${entry.index}-${entry.ticker}-${index}`">
                <td class="fw-bold">
                  <a :href="`https://finance.yahoo.com/quote/${entry.ticker}/`" target="_blank" rel="noopener noreferrer" class="text-primary text-decoration-none">
                    {{ entry.ticker }} <i class="fas fa-external-link-alt small ms-1"></i>
                  </a>
                </td>
                <td>
                  <span class="badge" :class="getQualifierMeta(entry.qualifier_type).badgeClass">
                    {{ getQualifierMeta(entry.qualifier_type).label }}
                  </span>
                </td>
                <td>
                  <span v-if="getDirectionMeta(entry.direction)" class="badge" :class="getDirectionMeta(entry.direction).badgeClass">
                    {{ getDirectionMeta(entry.direction).label }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td style="max-width: 480px; white-space: pre-wrap; word-break: break-word;">
                  {{ entry.reason || '—' }}
                </td>
                <td>
                  <span class="badge bg-light text-dark border">{{ entry.index }}</span>
                </td>
                <td class="text-nowrap">{{ formatDate(entry.timestamp_utc) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWatchlist, uploadWatchlistSuggestions, suggestWatchlistTicker, searchTickers } from '../services/api'
import { NAV_ITEMS } from '../constants/navItems'
import { getQualifierMeta, getDirectionMeta } from '../constants/watchlistQualifiers'
import DrawdownGuardBanner from '../components/DrawdownGuardBanner.vue'
import DrawdownGuardBadge from '../components/DrawdownGuardBadge.vue'
import { useDrawdownGuard } from '../composables/useDrawdownGuard'

const LOG_PREFIX = '[Watchlist]'

export default {
  name: 'Watchlist',
  components: { DrawdownGuardBanner, DrawdownGuardBadge },
  setup() {
    // Live drawdown-guard state, so manual entry controls can be halted while
    // the guard has blocked new entries for the session.
    const { guard: ddGuard, triggered: entryHalted } = useDrawdownGuard();
    return { ddGuard, entryHalted };
  },
  data() {
    return {
      entries: [],
      lastBuiltAt: null,
      loading: true,
      error: null,
      search: '',
      uploading: false,
      uploadResult: null,
      uploadError: null,
      defaultDirection: 'up',
      // One-off suggestion form
      tickerQuery: '',
      selectedTicker: null,
      suggestions: [],
      showDropdown: false,
      searchLoading: false,
      debounceTimer: null,
      manualDirection: 'up',
      manualSubmitting: false,
      manualError: null,
      manualResult: null,
      NAV_ITEMS,
    }
  },
  computed: {
    formattedLastBuiltAt() {
      return this.formatDate(this.lastBuiltAt) || 'Never';
    },
    filteredEntries() {
      const query = this.search.trim().toUpperCase();
      if (!query) return this.entries;
      return this.entries.filter(entry =>
        (entry.ticker || '').toUpperCase().includes(query)
      );
    },
    canSubmitManual() {
      return this.tickerQuery.trim().length > 0 && !this.manualSubmitting;
    },
    entryHaltTooltip() {
      return 'Trading halted by the portfolio gate — new entries are blocked for this session. Suggestions resume next session.';
    },
  },
  mounted() {
    this.loadWatchlist();
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer);
  },
  methods: {
    getQualifierMeta,
    getDirectionMeta,
    async loadWatchlist() {
      this.loading = true;
      this.error = null;
      try {
        const data = await fetchWatchlist();
        this.entries = Array.isArray(data?.entries) ? data.entries : [];
        this.lastBuiltAt = data?.last_built_at ?? null;
      } catch (err) {
        this.error = err.response?.data?.detail || err.message || 'Failed to load watchlist';
        console.error(`${LOG_PREFIX} Failed to fetch watchlist:`, this.error);
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.loadWatchlist();
    },
    triggerUpload() {
      if (this.entryHalted) return;
      this.$refs.csvInput.click();
    },
    async onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      // Reset the input so selecting the same file again re-triggers change.
      event.target.value = '';
      if (!file) return;

      this.uploading = true;
      this.uploadError = null;
      this.uploadResult = null;
      try {
        const result = await uploadWatchlistSuggestions(file, this.defaultDirection);
        this.uploadResult = {
          accepted: Array.isArray(result?.accepted) ? result.accepted : [],
          rejected: Array.isArray(result?.rejected) ? result.rejected : [],
          enqueued: result?.enqueued ?? 0,
        };
        // Reflect any tickers already qualified by an earlier suggestion.
        this.loadWatchlist();
      } catch (err) {
        this.uploadError = err.response?.data?.detail || err.message || 'Upload failed';
        console.error(`${LOG_PREFIX} CSV upload failed:`, this.uploadError);
      } finally {
        this.uploading = false;
      }
    },
    onTickerInput() {
      this.selectedTicker = null;
      const query = this.tickerQuery.trim();
      if (!query) {
        this.suggestions = [];
        this.showDropdown = false;
        clearTimeout(this.debounceTimer);
        return;
      }
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.runTickerSearch(query), 250);
    },
    async runTickerSearch(query) {
      this.searchLoading = true;
      try {
        const results = await searchTickers(query);
        this.suggestions = Array.isArray(results) ? results : [];
        this.showDropdown = this.suggestions.length > 0;
      } catch {
        this.suggestions = [];
        this.showDropdown = false;
      } finally {
        this.searchLoading = false;
      }
    },
    selectSuggestion(item) {
      this.selectedTicker = item;
      this.tickerQuery = item.ticker;
      this.suggestions = [];
      this.showDropdown = false;
    },
    onTickerBlur() {
      setTimeout(() => { this.showDropdown = false; }, 150);
    },
    async submitManual() {
      if (!this.canSubmitManual || this.entryHalted) return;
      const ticker = (this.selectedTicker?.ticker || this.tickerQuery).trim().toUpperCase();
      this.manualSubmitting = true;
      this.manualError = null;
      this.manualResult = null;
      try {
        const result = await suggestWatchlistTicker(ticker, this.manualDirection);
        const accepted = Array.isArray(result?.accepted) ? result.accepted : [];
        const rejected = Array.isArray(result?.rejected) ? result.rejected : [];
        if (accepted.length) {
          this.manualResult = {
            ok: true,
            message: `Queued ${ticker} for the researcher to qualify. It appears below once it passes news qualification.`,
          };
          this.tickerQuery = '';
          this.selectedTicker = null;
          this.loadWatchlist();
        } else {
          const reason = rejected.length ? rejected[0].reason : 'Not queued.';
          this.manualResult = { ok: false, message: `${ticker} was not queued — ${reason}` };
        }
      } catch (err) {
        this.manualError = err.response?.data?.detail || err.message || 'Suggestion failed';
        console.error(`${LOG_PREFIX} Single suggestion failed:`, this.manualError);
      } finally {
        this.manualSubmitting = false;
      }
    },
    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      return date.toLocaleString();
    }
  }
}
</script>

<style scoped>
.watchlist-search {
  width: 220px;
}
.csv-direction {
  width: 150px;
}
</style>
